"use client";

import { Link } from "react-router-dom";
import { useMemo, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { api } from "@/lib/axios";
import { useCategoryTree } from "@/hooks/api/useCategoryTree";
import { useProducts } from "@/hooks/api/useProducts";
import { flattenCategories } from "@/lib/categories";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, ImageIcon, Pencil, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { apiBaseURL } from "@/lib/api-config";
import type { ProductRow } from "@/lib/types/product";

function productImageSrc(url: string | null | undefined): string | null {
  if (!url) {
    return null;
  }
  if (url.startsWith("http")) {
    return url;
  }

  return `${apiBaseURL}${url}`;
}

function ProductThumb({
  imageUrl,
  name,
  size = "md",
  onClick,
}: {
  imageUrl: string | null | undefined;
  name: string;
  size?: "sm" | "md";
  onClick?: () => void;
}) {
  const src = productImageSrc(imageUrl);
  const box = size === "sm" ? "h-8 w-8" : "h-10 w-10";
  const clickable = Boolean(onClick && src);

  if (!src) {
    return (
      <div
        className={cn(
          "flex shrink-0 items-center justify-center rounded-md border border-border bg-muted text-muted-foreground",
          box,
        )}
        aria-hidden
      >
        <ImageIcon className="h-4 w-4" />
      </div>
    );
  }

  const img = (
    <img
      src={src}
      alt={name}
      className={cn(
        "shrink-0 rounded-md border border-border object-cover",
        box,
        clickable && "transition-opacity hover:opacity-90",
      )}
    />
  );

  if (!clickable) {
    return img;
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className="shrink-0 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      aria-label={`View image for ${name}`}
    >
      {img}
    </button>
  );
}

type Offer = {
  id: number;
  name: string;
  offer_type: "product" | "category" | "bogo";
  discount_type: "pct" | "flat";
  discount_value: string;
  product_id: number | null;
  category_id: number | null;
  buy_qty: number;
  get_qty: number;
  valid_from: string;
  valid_until: string;
  is_active: number;
};

type OfferForm = {
  name: string;
  offer_type: Offer["offer_type"];
  discount_type: Offer["discount_type"];
  discount_value: string;
  product_id: string;
  category_id: string;
  buy_qty: string;
  get_qty: string;
  valid_from: string;
  valid_until: string;
  is_active: string;
};

const today = () => new Date().toISOString().slice(0, 10);

const emptyForm = (): OfferForm => ({
  name: "",
  offer_type: "product",
  discount_type: "pct",
  discount_value: "",
  product_id: "",
  category_id: "",
  buy_qty: "1",
  get_qty: "1",
  valid_from: today(),
  valid_until: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
  is_active: "1",
});

function offerToForm(o: Offer): OfferForm {
  return {
    name: o.name,
    offer_type: o.offer_type,
    discount_type: o.discount_type,
    discount_value: String(o.discount_value),
    product_id: o.product_id != null ? String(o.product_id) : "",
    category_id: o.category_id != null ? String(o.category_id) : "",
    buy_qty: String(o.buy_qty ?? 1),
    get_qty: String(o.get_qty ?? 0),
    valid_from: o.valid_from.slice(0, 10),
    valid_until: o.valid_until.slice(0, 10),
    is_active: o.is_active ? "1" : "0",
  };
}

function formToPayload(f: OfferForm) {
  const discountValue = Number(f.discount_value);
  return {
    name: f.name.trim(),
    offer_type: f.offer_type,
    discount_type: f.discount_type,
    discount_value: Number.isFinite(discountValue) ? discountValue : 0,
    product_id: f.offer_type === "category" ? null : f.product_id ? Number(f.product_id) : null,
    category_id: f.offer_type === "category" && f.category_id ? Number(f.category_id) : null,
    buy_qty: f.offer_type === "bogo" ? Math.max(1, Number(f.buy_qty) || 1) : 1,
    get_qty: f.offer_type === "bogo" ? Math.max(0, Number(f.get_qty) || 0) : 0,
    valid_from: f.valid_from,
    valid_until: f.valid_until,
    is_active: f.is_active === "1" ? 1 : 0,
  };
}

function offerStatus(o: Offer): "active" | "scheduled" | "expired" | "inactive" {
  if (!o.is_active) {
    return "inactive";
  }
  const now = Date.now();
  const from = new Date(o.valid_from).getTime();
  const until = new Date(o.valid_until).getTime();
  if (until < now) {
    return "expired";
  }
  if (from > now) {
    return "scheduled";
  }
  return "active";
}

function OfferFormFields({
  form,
  setForm,
  products,
  categories,
  onPreviewProduct,
}: {
  form: OfferForm;
  setForm: Dispatch<SetStateAction<OfferForm>>;
  products: { id: number; name: string; image_url: string | null }[];
  categories: { id: number; label: string }[];
  onPreviewProduct?: (product: { name: string; image_url: string | null }) => void;
}) {
  const selectedProduct =
    form.offer_type === "product" || form.offer_type === "bogo"
      ? products.find((p) => String(p.id) === form.product_id)
      : undefined;

  return (
    <div className="space-y-3 py-2">
      <div className="grid gap-2">
        <Label htmlFor="offer-name">Name</Label>
        <Input
          id="offer-name"
          value={form.name}
          onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
          placeholder="Summer sale"
        />
      </div>
      <div className="grid gap-2">
        <Label htmlFor="offer-type">Type</Label>
        <select
          id="offer-type"
          className="flex h-9 w-full rounded-lg border border-input bg-background px-2 text-sm"
          value={form.offer_type}
          onChange={(e) =>
            setForm((s) => ({
              ...s,
              offer_type: e.target.value as Offer["offer_type"],
            }))
          }
        >
          <option value="product">Product discount</option>
          <option value="category">Category discount</option>
          <option value="bogo">Buy X get Y (BOGO)</option>
        </select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="grid gap-2">
          <Label htmlFor="discount-type">Discount</Label>
          <select
            id="discount-type"
            className="flex h-9 w-full rounded-lg border border-input bg-background px-2 text-sm"
            value={form.discount_type}
            onChange={(e) =>
              setForm((s) => ({
                ...s,
                discount_type: e.target.value as Offer["discount_type"],
              }))
            }
          >
            <option value="pct">Percent (%)</option>
            <option value="flat">Flat (₹)</option>
          </select>
        </div>
        <div className="grid gap-2">
          <Label htmlFor="discount-value">Value</Label>
          <Input
            id="discount-value"
            type="number"
            min="0"
            step="0.01"
            value={form.discount_value}
            onChange={(e) => setForm((s) => ({ ...s, discount_value: e.target.value }))}
          />
        </div>
      </div>
      {(form.offer_type === "product" || form.offer_type === "bogo") && (
        <div className="grid gap-2">
          <Label htmlFor="offer-product">Product</Label>
          <select
            id="offer-product"
            className="flex h-9 w-full rounded-lg border border-input bg-background px-2 text-sm"
            value={form.product_id}
            onChange={(e) => setForm((s) => ({ ...s, product_id: e.target.value }))}
          >
            <option value="">— Select product —</option>
            {products.map((p) => (
              <option key={p.id} value={String(p.id)}>
                {p.name}
              </option>
            ))}
          </select>
          {selectedProduct ? (
            <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/30 p-2">
              <ProductThumb
                imageUrl={selectedProduct.image_url}
                name={selectedProduct.name}
                onClick={
                  onPreviewProduct
                    ? () => onPreviewProduct(selectedProduct)
                    : undefined
                }
              />
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{selectedProduct.name}</p>
                <p className="text-xs text-muted-foreground">Offer product</p>
              </div>
            </div>
          ) : null}
        </div>
      )}
      {form.offer_type === "category" && (
        <div className="grid gap-2">
          <Label htmlFor="offer-category">Category</Label>
          <select
            id="offer-category"
            className="flex h-9 w-full rounded-lg border border-input bg-background px-2 text-sm"
            value={form.category_id}
            onChange={(e) => setForm((s) => ({ ...s, category_id: e.target.value }))}
          >
            <option value="">— Select category —</option>
            {categories.map((c) => (
              <option key={c.id} value={String(c.id)}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
      )}
      {form.offer_type === "bogo" && (
        <div className="grid grid-cols-2 gap-3">
          <div className="grid gap-2">
            <Label htmlFor="buy-qty">Buy qty</Label>
            <Input
              id="buy-qty"
              type="number"
              min="1"
              value={form.buy_qty}
              onChange={(e) => setForm((s) => ({ ...s, buy_qty: e.target.value }))}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="get-qty">Free qty</Label>
            <Input
              id="get-qty"
              type="number"
              min="0"
              value={form.get_qty}
              onChange={(e) => setForm((s) => ({ ...s, get_qty: e.target.value }))}
            />
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div className="grid gap-2">
          <Label htmlFor="valid-from">Valid from</Label>
          <Input
            id="valid-from"
            type="date"
            value={form.valid_from}
            onChange={(e) => setForm((s) => ({ ...s, valid_from: e.target.value }))}
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="valid-until">Valid until</Label>
          <Input
            id="valid-until"
            type="date"
            min={form.valid_from || today()}
            value={form.valid_until}
            onChange={(e) => setForm((s) => ({ ...s, valid_until: e.target.value }))}
          />
        </div>
      </div>
      <div className="grid gap-2">
        <Label htmlFor="offer-active">Status</Label>
        <select
          id="offer-active"
          className="flex h-9 w-full rounded-lg border border-input bg-background px-2 text-sm"
          value={form.is_active}
          onChange={(e) => setForm((s) => ({ ...s, is_active: e.target.value }))}
        >
          <option value="1">Active</option>
          <option value="0">Inactive</option>
        </select>
      </div>
    </div>
  );
}


export default function MarketingOffersPage() {
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [editOffer, setEditOffer] = useState<Offer | null>(null);
  const [createForm, setCreateForm] = useState<OfferForm>(emptyForm);
  const [editForm, setEditForm] = useState<OfferForm>(emptyForm);
  const [err, setErr] = useState<string | null>(null);
  const [previewProduct, setPreviewProduct] = useState<{
    name: string;
    image_url: string | null;
  } | null>(null);

  const { data: tree = [] } = useCategoryTree();
  const categories = useMemo(() => flattenCategories(tree), [tree]);
  const { data: prodData } = useProducts({ page: 1, per_page: 200, include_inactive: true });
  const products = prodData?.data ?? [];

  const productById = useMemo(() => {
    const m = new Map<number, ProductRow>();
    for (const p of products) {
      m.set(p.id, p);
    }
    return m;
  }, [products]);

  const categoryName = useMemo(() => {
    const m = new Map<number, string>();
    for (const c of categories) {
      m.set(c.id, c.label);
    }
    return m;
  }, [categories]);

  const { data, isLoading } = useQuery({
    queryKey: ["offers", "manage"],
    queryFn: async () => {
      const { data: d } = await api.get<{ offers: Offer[] }>("/api/offers/manage");
      return d.offers;
    },
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["offers"] });

  const parseErr = (e: unknown) =>
    axios.isAxiosError(e)
      ? (e.response?.data as { error?: string } | undefined)?.error ?? e.message
      : "Request failed";

  const createMut = useMutation({
    mutationFn: async () => {
      setErr(null);
      await api.post("/api/offers", formToPayload(createForm));
    },
    onSuccess: async () => {
      setCreateForm(emptyForm());
      setCreateOpen(false);
      await invalidate();
    },
    onError: (e) => setErr(parseErr(e)),
  });

  const updateMut = useMutation({
    mutationFn: async () => {
      if (!editOffer) {
        return;
      }
      setErr(null);
      await api.put(`/api/offers/${editOffer.id}`, formToPayload(editForm));
    },
    onSuccess: async () => {
      setEditOffer(null);
      await invalidate();
    },
    onError: (e) => setErr(parseErr(e)),
  });

  const deleteMut = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/offers/${id}`);
    },
    onSuccess: async () => {
      await invalidate();
    },
  });

  const statusClass: Record<ReturnType<typeof offerStatus>, string> = {
    active: "text-emerald-600 dark:text-emerald-400",
    scheduled: "text-amber-600 dark:text-amber-400",
    expired: "text-muted-foreground",
    inactive: "text-muted-foreground",
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link
          to="/marketing"
          className={cn(buttonVariants({ variant: "ghost", size: "sm" }), "gap-2 inline-flex")}
        >
          <ArrowLeft className="h-4 w-4" />
          Marketing
        </Link>
        <Dialog
          open={createOpen}
          onOpenChange={(o) => {
            setCreateOpen(o);
            if (!o) {
              setErr(null);
              setCreateForm(emptyForm());
            }
          }}
        >
          <DialogTrigger>
            <Button>New offer</Button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Create offer</DialogTitle>
            </DialogHeader>
            <OfferFormFields
              form={createForm}
              setForm={setCreateForm}
              products={products}
              categories={categories}
              onPreviewProduct={setPreviewProduct}
            />
            {err && createOpen && (
              <p className="text-sm text-destructive" role="alert">
                {err}
              </p>
            )}
            <Button
              type="button"
              disabled={createMut.isPending || createForm.name.trim() === ""}
              onClick={() => createMut.mutate()}
            >
              {createMut.isPending ? "Saving…" : "Create"}
            </Button>
          </DialogContent>
        </Dialog>
      </div>

      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Offers</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Product, category, and BOGO promotions. POS uses active offers in date window.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">All offers</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : (
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left">
                  <tr>
                    <th className="p-3 font-medium">Name</th>
                    <th className="p-3 font-medium">Type</th>
                    <th className="p-3 font-medium">Discount</th>
                    <th className="p-3 font-medium">Target</th>
                    <th className="p-3 font-medium">Valid</th>
                    <th className="p-3 font-medium">Status</th>
                    <th className="p-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(data ?? []).map((o) => {
                    const st = offerStatus(o);
                    const product =
                      o.offer_type !== "category" && o.product_id != null
                        ? productById.get(o.product_id)
                        : undefined;
                    const target =
                      o.offer_type === "category"
                        ? categoryName.get(o.category_id ?? 0) ?? "—"
                        : product?.name ?? "—";
                    return (
                      <tr key={o.id} className="border-t">
                        <td className="p-3 font-medium">{o.name}</td>
                        <td className="p-3 capitalize text-muted-foreground">{o.offer_type}</td>
                        <td className="p-3 tabular-nums">
                          {o.discount_type === "pct"
                            ? `${o.discount_value}%`
                            : `₹${o.discount_value}`}
                          {o.offer_type === "bogo" && (
                            <span className="text-muted-foreground text-xs block">
                              Buy {o.buy_qty} get {o.get_qty}
                            </span>
                          )}
                        </td>
                        <td className="p-3">
                          {product ? (
                            <div className="flex items-center gap-2.5 min-w-0">
                              <ProductThumb
                                imageUrl={product.image_url}
                                name={product.name}
                                onClick={() => setPreviewProduct(product)}
                              />
                              <span className="truncate text-muted-foreground" title={target}>
                                {target}
                              </span>
                            </div>
                          ) : (
                            <span className="text-muted-foreground">{target}</span>
                          )}
                        </td>
                        <td className="p-3 text-xs text-muted-foreground whitespace-nowrap">
                          {o.valid_from.slice(0, 10)} → {o.valid_until.slice(0, 10)}
                        </td>
                        <td className={cn("p-3 capitalize text-xs font-medium", statusClass[st])}>
                          {st}
                        </td>
                        <td className="p-3">
                          <div className="flex justify-end gap-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon-sm"
                              aria-label="Edit offer"
                              onClick={() => {
                                setErr(null);
                                setEditOffer(o);
                                setEditForm(offerToForm(o));
                              }}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon-sm"
                              aria-label="Delete offer"
                              disabled={deleteMut.isPending}
                              onClick={() => {
                                if (window.confirm(`Delete offer "${o.name}"?`)) {
                                  deleteMut.mutate(o.id);
                                }
                              }}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {!isLoading && (data ?? []).length === 0 && (
                <p className="p-4 text-sm text-muted-foreground">No offers yet. Create one above.</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={editOffer !== null}
        onOpenChange={(o) => {
          if (!o) {
            setEditOffer(null);
            setErr(null);
          }
        }}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit offer</DialogTitle>
          </DialogHeader>
          <OfferFormFields
            form={editForm}
            setForm={setEditForm}
            products={products}
            categories={categories}
            onPreviewProduct={setPreviewProduct}
          />
          {err && editOffer && (
            <p className="text-sm text-destructive" role="alert">
              {err}
            </p>
          )}
          <Button
            type="button"
            disabled={updateMut.isPending || editForm.name.trim() === ""}
            onClick={() => updateMut.mutate()}
          >
            {updateMut.isPending ? "Saving…" : "Save changes"}
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog
        open={previewProduct !== null}
        onOpenChange={(o) => {
          if (!o) {
            setPreviewProduct(null);
          }
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="line-clamp-1">{previewProduct?.name ?? "Product"}</DialogTitle>
          </DialogHeader>
          <div className="flex min-h-[240px] items-center justify-center rounded-lg border bg-muted/40 p-4">
            {previewProduct && productImageSrc(previewProduct.image_url) ? (
              <img
                src={productImageSrc(previewProduct.image_url)!}
                alt={previewProduct.name}
                className="max-h-[70vh] w-auto max-w-full rounded-md object-contain"
              />
            ) : (
              <p className="text-sm text-muted-foreground">No image for this product.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
